import { defineStore } from 'pinia';
import { ref } from 'vue';
import type {
  Fetch,
  Team,
  TeamMember,
  TeamInvite,
  TeamCalendar,
  TeamCreate,
  TeamUpdate,
  TeamInviteCreate,
  TeamCalendarCreate,
  TeamMemberAdd,
  TeamMemberCandidate,
  TeamCalendarCandidate,
  Schedule,
} from '@/models';

export const useTeamStore = defineStore('teams', () => {
  const call = ref<Fetch | null>(null);
  const isLoaded = ref(false);

  const teams = ref<Team[]>([]);
  const activeTeamId = ref<number | null>(null);
  const activeTeam = ref<Team | null>(null);
  const members = ref<TeamMember[]>([]);
  const invites = ref<TeamInvite[]>([]);
  const calendars = ref<TeamCalendar[]>([]);
  const lastInvite = ref<TeamInvite | null>(null);
  const schedulesByTeam = ref<Record<number, Schedule[]>>({});
  const memberCandidates = ref<TeamMemberCandidate[]>([]);
  const calendarCandidates = ref<TeamCalendarCandidate[]>([]);
  const lastError = ref<string | null>(null);

  const init = (fetch: Fetch) => {
    call.value = fetch;
  };

  const fetchTeams = async (force = false) => {
    if (isLoaded.value && !force) return;
    try {
      const { data, error } = await call.value('team/my').get().json();
      if (error.value) {
        lastError.value = data?.value?.detail ?? 'Failed to load teams';
        return;
      }
      if (data.value) {
        teams.value = data.value;
        isLoaded.value = true;
        lastError.value = null;
      }
    } catch (err: any) {
      lastError.value = err?.message ?? 'Failed to load teams';
    }
  };

  const selectTeam = async (teamId: number) => {
    activeTeamId.value = teamId;
    const { data, error } = await call.value(`team/${teamId}`).get().json();
    if (!error.value && data.value) {
      activeTeam.value = data.value;
    }
    await Promise.all([
      fetchMembers(teamId),
      fetchInvites(teamId),
      fetchCalendars(teamId),
      fetchCalendarCandidates(teamId),
      fetchSchedules(teamId),
    ]);
  };

  const createTeam = async (payload: TeamCreate) => {
    const { data, error } = await call.value('team').post(payload).json();
    if (!error.value && data.value) {
      teams.value = [...teams.value, data.value];
    }
    return { data, error };
  };

  const updateTeam = async (teamId: number, payload: TeamUpdate) => {
    const { data, error } = await call.value(`team/${teamId}`).put(payload).json();
    if (!error.value && data.value) {
      teams.value = teams.value.map((team) => (team.id === teamId ? data.value : team));
      if (activeTeam.value?.id === teamId) {
        activeTeam.value = data.value;
      }
    }
    return { data, error };
  };

  const deleteTeam = async (teamId: number) => {
    const response = await call.value(`team/${teamId}`).delete().json();
    teams.value = teams.value.filter((team) => team.id !== teamId);
    if (activeTeamId.value === teamId) {
      activeTeamId.value = null;
      activeTeam.value = null;
      members.value = [];
      invites.value = [];
      calendars.value = [];
    }
    return response;
  };

  const fetchMembers = async (teamId: number) => {
    const { data, error } = await call.value(`team/${teamId}/members`).get().json();
    if (!error.value && data.value) {
      members.value = data.value;
    } else {
      members.value = [];
    }
  };

  const fetchInvites = async (teamId: number) => {
    const { data, error } = await call.value(`team/${teamId}/invites`).get().json();
    if (!error.value && data.value) {
      invites.value = data.value;
    } else {
      invites.value = [];
    }
  };

  const fetchCalendars = async (teamId: number) => {
    const { data, error } = await call.value(`team/${teamId}/calendars`).get().json();
    if (!error.value && data.value) {
      calendars.value = data.value;
    } else {
      calendars.value = [];
    }
  };

  const fetchCalendarCandidates = async (teamId: number) => {
    const { data, error } = await call.value(`team/${teamId}/calendar-candidates`).get().json();
    if (!error.value && data.value) {
      calendarCandidates.value = data.value;
    } else {
      calendarCandidates.value = [];
    }
    return { data, error };
  };

  const inviteMember = async (teamId: number, payload: TeamInviteCreate) => {
    const { data, error } = await call.value(`team/${teamId}/members/invite`).post(payload).json();
    if (!error.value && data.value) {
      invites.value = [...invites.value, data.value];
      lastInvite.value = data.value;
    }
    return { data, error };
  };

  const fetchMemberCandidates = async (teamId: number, query: string) => {
    const q = encodeURIComponent(query ?? '');
    const { data, error } = await call.value(`team/${teamId}/member-candidates?q=${q}&limit=25`).get().json();
    if (!error.value && data.value) {
      memberCandidates.value = data.value;
    } else {
      memberCandidates.value = [];
    }
    return { data, error };
  };

  const addMember = async (teamId: number, payload: TeamMemberAdd) => {
    const { data, error } = await call.value(`team/${teamId}/members`).post(payload).json();
    if (!error.value && data.value) {
      members.value = [...members.value, data.value];
      memberCandidates.value = memberCandidates.value.filter((candidate) => candidate.id !== payload.subscriber_id);
    }
    return { data, error };
  };

  const acceptInvite = async (token: string) => {
    return call.value(`team/invites/${token}/accept`).post({}).json();
  };

  const updateMemberRole = async (teamId: number, memberId: number, role: string) => {
    return call.value(`team/${teamId}/members/${memberId}/role?role=${role}`).post({}).json();
  };

  const removeMember = async (teamId: number, memberId: number) => {
    const response = await call.value(`team/${teamId}/members/${memberId}`).delete().json();
    members.value = members.value.filter((member) => member.id !== memberId);
    return response;
  };

  const addCalendar = async (teamId: number, payload: TeamCalendarCreate) => {
    const { data, error } = await call.value(`team/${teamId}/calendars`).post(payload).json();
    if (!error.value && data.value) {
      calendars.value = [...calendars.value, data.value];
      calendarCandidates.value = calendarCandidates.value.filter((candidate) => candidate.id !== payload.calendar_id);
    }
    return { data, error };
  };

  const removeCalendar = async (teamId: number, teamCalendarId: number) => {
    const response = await call.value(`team/${teamId}/calendars/${teamCalendarId}`).delete().json();
    calendars.value = calendars.value.filter((calendar) => calendar.id !== teamCalendarId);
    await fetchCalendarCandidates(teamId);
    return response;
  };

  const fetchSchedules = async (teamId: number) => {
    const { data, error } = await call.value(`team/${teamId}/schedules`).get().json();
    if (!error.value && data.value) {
      schedulesByTeam.value = {
        ...schedulesByTeam.value,
        [teamId]: data.value,
      };
    }
  };

  const createTeamSchedule = async (teamId: number, payload: Partial<Schedule>) => {
    const response = await call.value(`team/${teamId}/schedules`).post(payload).json();
    await fetchSchedules(teamId);
    return response;
  };

  const updateTeamSchedule = async (teamId: number, scheduleId: number, payload: Partial<Schedule>) => {
    const response = await call.value(`team/${teamId}/schedules/${scheduleId}`).put(payload).json();
    await fetchSchedules(teamId);
    return response;
  };

  const deleteTeamSchedule = async (teamId: number, scheduleId: number) => {
    const response = await call.value(`team/${teamId}/schedules/${scheduleId}`).delete().json();
    await fetchSchedules(teamId);
    return response;
  };

  const $reset = () => {
    teams.value = [];
    activeTeamId.value = null;
    activeTeam.value = null;
    members.value = [];
    invites.value = [];
    calendars.value = [];
    lastInvite.value = null;
    schedulesByTeam.value = {};
    memberCandidates.value = [];
    calendarCandidates.value = [];
    isLoaded.value = false;
    lastError.value = null;
  };

  return {
    init,
    fetchTeams,
    selectTeam,
    createTeam,
    updateTeam,
    deleteTeam,
    fetchMembers,
    fetchInvites,
    fetchCalendars,
    fetchCalendarCandidates,
    fetchMemberCandidates,
    addMember,
    inviteMember,
    acceptInvite,
    updateMemberRole,
    removeMember,
    addCalendar,
    removeCalendar,
    fetchSchedules,
    createTeamSchedule,
    updateTeamSchedule,
    deleteTeamSchedule,
    $reset,
    teams,
    activeTeamId,
    activeTeam,
    members,
    invites,
    calendars,
    lastInvite,
    schedulesByTeam,
    memberCandidates,
    calendarCandidates,
    lastError,
  };
});

export const createTeamStore = (call: Fetch) => {
  const store = useTeamStore();
  store.init(call);
  return store;
};
